import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Send, Paperclip, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const inputCls = "bg-white border-[var(--ir-border)] rounded-none focus-visible:ring-[var(--ir-blue)]";
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ["Referral Tracking", "Commission or Payment", "Marketing Materials", "Account Access", "General Question"];
const STATUSES = ["Open", "In Progress", "Closed"];
const statusColor = { Open: "bg-[var(--ir-orange)]/10 text-[var(--ir-orange)] border-[var(--ir-orange)]/30", "In Progress": "bg-[var(--ir-blue)]/10 text-[var(--ir-blue)] border-[var(--ir-blue)]/25", Closed: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30" };

const AdminTickets = ({ onChange }) => {
  const [tickets, setTickets] = useState([]);
  const [filters, setFilters] = useState({ category: "all", status: "all" });
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [replyFiles, setReplyFiles] = useState([]);

  const load = useCallback(() => {
    const params = {};
    if (filters.category !== "all") params.category = filters.category;
    if (filters.status !== "all") params.status = filters.status;
    api.get("/admin/tickets", { params }).then((r) => setTickets(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const open = (id) => api.get(`/admin/tickets/${id}`).then((r) => { setActive(r.data); setReply(""); setReplyFiles([]); }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));

  const sendReply = async () => {
    if (!reply.trim()) return;
    const fd = new FormData(); fd.append("message", reply);
    [...replyFiles].slice(0, 3).forEach((f) => fd.append("files", f));
    try { await api.post(`/admin/tickets/${active.id}/reply`, fd); setReply(""); setReplyFiles([]); open(active.id); load(); onChange?.(); toast.success("Reply sent"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const setStatus = async (status) => {
    try { await api.patch(`/admin/tickets/${active.id}/status`, { status }); setActive({ ...active, status }); load(); onChange?.(); toast.success(`Status: ${status}`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (active) {
    return (
      <div data-testid="admin-ticket-thread" className="max-w-2xl">
        <div className="flex items-center justify-between mb-4">
          <Button onClick={() => setActive(null)} variant="outline" className="border-[var(--ir-border)] bg-white text-[var(--ir-text)] rounded-none" data-testid="admin-back-tickets"><ArrowLeft className="w-4 h-4 mr-2" />All tickets</Button>
          <Select value={active.status} onValueChange={setStatus}>
            <SelectTrigger className={`w-40 ${inputCls}`} data-testid="admin-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`admin-status-opt-${s}`}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="bg-white border border-[var(--ir-border)] rounded-lg soft-shadow p-6">
          <h3 className="font-semibold text-[var(--ir-text)]">{active.subject}</h3>
          <p className="text-[var(--ir-muted)] text-xs mb-4">{active.category} · {active.affiliate_name} ({active.affiliate_email})</p>
          <div className="space-y-3">
            {active.messages.map((m) => (
              <div key={m.id} className={`p-3 rounded ${m.sender_role === "admin" ? "bg-[var(--ir-blue)]/5 border border-[var(--ir-blue)]/15" : "bg-[var(--ir-surface2)]"}`} data-testid={`admin-msg-${m.id}`}>
                <div className="flex items-center justify-between"><span className="text-xs font-semibold text-[var(--ir-text)]">{m.sender_role === "admin" ? "Support" : m.sender_name}</span><span className="text-[var(--ir-muted)] text-[10px]">{m.created_at ? new Date(m.created_at).toLocaleString() : ""}</span></div>
                <p className="text-[var(--ir-text)] text-sm whitespace-pre-wrap mt-1">{m.message}</p>
                {m.attachments.map((a, i) => (
                  <a key={i} href={`${BACKEND}/api/tickets/${active.id}/attachments/${m.id}/${i}`} className="inline-flex items-center gap-1 text-[var(--ir-blue)] text-xs mt-2 mr-3 underline" data-testid={`admin-att-${m.id}-${i}`}><Paperclip className="w-3 h-3" />{a.filename}</a>
                ))}
              </div>
            ))}
          </div>
          <div className="mt-4 border-t border-[var(--ir-border)] pt-4 space-y-2">
            <Textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3} placeholder="Reply to affiliate…" className={inputCls} data-testid="admin-reply-input" />
            <Input type="file" multiple onChange={(e) => setReplyFiles(e.target.files)} className={inputCls} data-testid="admin-reply-files" />
            <Button onClick={sendReply} disabled={!reply.trim()} className="bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] text-white rounded-none disabled:opacity-50" data-testid="admin-send-reply-btn"><Send className="w-4 h-4 mr-2" />Send reply</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-tickets">
      <div className="flex flex-wrap gap-3 mb-4">
        <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
          <SelectTrigger className={`w-44 ${inputCls}`} data-testid="filter-status-select"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
          <SelectTrigger className={`w-56 ${inputCls}`} data-testid="filter-category-select"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All categories</SelectItem>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="bg-white border border-[var(--ir-border)] rounded-lg soft-shadow divide-y divide-[var(--ir-border)]" data-testid="admin-ticket-list">
        {tickets.length === 0 && <div className="p-8 text-center text-[var(--ir-muted)] text-sm">No tickets match.</div>}
        {tickets.map((t) => (
          <button key={t.id} onClick={() => open(t.id)} className={`w-full text-left p-4 hover:bg-[var(--ir-surface2)] flex items-center justify-between gap-3 ${t.awaiting_admin ? "border-l-4 border-[var(--ir-orange)]" : ""}`} data-testid={`admin-ticket-item-${t.id}`}>
            <div>
              <div className="text-[var(--ir-text)] text-sm font-medium flex items-center gap-2">{t.awaiting_admin && <AlertCircle className="w-4 h-4 text-[var(--ir-orange)]" data-testid={`awaiting-${t.id}`} />}{t.subject}</div>
              <div className="text-[var(--ir-muted)] text-xs">{t.category} · {t.affiliate_name}</div>
            </div>
            <div className="flex items-center gap-2">
              {t.awaiting_admin && <Badge className="rounded-none border bg-[var(--ir-orange)]/10 text-[var(--ir-orange)] border-[var(--ir-orange)]/30 text-[10px]">Awaiting reply</Badge>}
              <Badge className={`rounded-none border text-[10px] ${statusColor[t.status] || statusColor.Open}`}>{t.status}</Badge>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default AdminTickets;
